import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-user-row.css?inline';

/**
 * Per-user record passed via the `user` attribute as a JSON string. The
 * shape is intentionally loose — only `email` is required; the rest are
 * surfaced if present so the same component fits both ActivityLog and
 * Momentus rosters.
 */
export interface PkUserRecord {
    id?:            string;
    email:          string;
    role?:          number | string;
    status?:        string;
    emailVerified?: boolean;
    totpEnabled?:   boolean;
    createdAt?:     string;
}

export interface PkUserRowAction {
    label:        string;
    action:       string;
    destructive?: boolean;
}

/**
 * `<pk-user-row>` — admin user table row with inline role-select + actions.
 *
 * Renders as a flex row (intended to sit inside a list parent). The user
 * record + role options + actions are all JSON-attribute driven so the
 * component is friendly to Razor server-side rendering — the host inlines
 * a JSON string and the component does the rest.
 *
 * Attributes:
 *   - `user`         (JSON) — `PkUserRecord`. Required.
 *   - `role-options` (JSON) — `Array<{ value, label }>` for the role select.
 *   - `actions`      (JSON) — `PkUserRowAction[]` for the action buttons.
 *
 * Events:
 *   - `pk-user-action` — fired when an action button is clicked or the role
 *                        select fires `change`.
 *                        `detail: { userId, action, role? }`.
 *                        For role changes: `action: 'role.change'`,
 *                                          `role: <newValue>`.
 */
export class PkUserRow extends HTMLElement {
    static readonly tagName = 'pk-user-row';

    static get observedAttributes(): string[] {
        return ['user', 'role-options', 'actions'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private _parseUser(): PkUserRecord {
        const raw = this.getAttribute('user');
        if (!raw) return { email: '' };
        try {
            const parsed = JSON.parse(raw) as PkUserRecord;
            return parsed && typeof parsed === 'object' ? parsed : { email: '' };
        } catch {
            return { email: '' };
        }
    }

    private _parseJsonArray<T>(name: string): T[] {
        const raw = this.getAttribute(name);
        if (!raw) return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed as T[] : [];
        } catch { return []; }
    }

    private _emit(action: string, role?: number | string): void {
        const user = this._parseUser();
        this.dispatchEvent(new CustomEvent('pk-user-action', {
            detail: {
                userId: user.id ?? user.email,
                action,
                ...(role !== undefined ? { role } : {}),
            },
            bubbles:  true,
            composed: true,
        }));
    }

    private readonly _onRoleChange = (e: Event): void => {
        const sel = e.target as HTMLSelectElement;
        const val = sel.value;
        // Preserve numeric role bytes when the option value parses as a number.
        const numeric = Number(val);
        const role: number | string = Number.isFinite(numeric) && String(numeric) === val
            ? numeric
            : val;
        this._emit('role.change', role);
    };

    private template(): TemplateResult {
        const user        = this._parseUser();
        const roleOptions = this._parseJsonArray<{ value: number | string; label: string }>('role-options');
        const actions     = this._parseJsonArray<PkUserRowAction>('actions');
        const currentRole = user.role !== undefined ? String(user.role) : '';

        return html`
            <style>${styles}</style>
            <div class="row" role="row">
                <div class="cell cell-email" role="cell">
                    <span class="email">${user.email}</span>
                    ${user.status ? html`<span class="status">${user.status}</span>` : ''}
                </div>
                <div class="cell cell-role" role="cell">
                    ${roleOptions.length > 0 ? html`
                        <select class="role-select" @change=${this._onRoleChange} aria-label="Role">
                            ${roleOptions.map(o => html`
                                <option value=${String(o.value)}
                                    ?selected=${String(o.value) === currentRole}
                                >${o.label}</option>
                            `)}
                        </select>
                    ` : html`<span class="role-static">${user.role ?? '—'}</span>`}
                </div>
                <div class="cell cell-flags" role="cell">
                    <span class="flag ${user.emailVerified ? 'flag-on' : 'flag-off'}"
                          title="Email verified">${user.emailVerified ? '✓' : '—'} email</span>
                    <span class="flag ${user.totpEnabled ? 'flag-on' : 'flag-off'}"
                          title="TOTP enabled">${user.totpEnabled ? '✓' : '—'} TOTP</span>
                </div>
                <div class="cell cell-actions" role="cell">
                    ${actions.map(a => html`
                        <button
                            type="button"
                            class="action-btn ${a.destructive ? 'destructive' : ''}"
                            data-action=${a.action}
                            @click=${() => this._emit(a.action)}
                        >${a.label}</button>
                    `)}
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkUserRow.tagName)) {
    customElements.define(PkUserRow.tagName, PkUserRow);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-user-row': PkUserRow;
    }
}
